import React from 'react';
import { isDisqualified, isReviewed, isFlagged, isPlaced, type ReviewCatch } from '@shared/adjudication';

export interface CatchCardStatus {
  /** Tailwind border classes for the card. */
  borderClass: string;
  /** The text label shown on the card (colour is never the only signal). */
  label: string;
  /** One of these four states, for tests and callers that need to branch. */
  state: 'disqualified' | 'needsReview' | 'checked' | 'neutral';
}

/**
 * Card border/label for a catch (#144 R4): red for disqualified, amber for
 * needs review (placed or flagged, not checked), green for checked by a
 * person, neutral otherwise. Missing GPS never colours the card.
 */
export function getCatchCardStatus(c: ReviewCatch, rank: number | null | undefined): CatchCardStatus {
  if (isDisqualified(c)) {
    return { borderClass: 'border-l-4 border-red-500', label: 'Disqualified', state: 'disqualified' };
  }
  if (isReviewed(c)) {
    return { borderClass: 'border-l-4 border-green-500', label: 'Checked', state: 'checked' };
  }
  if (isPlaced(rank) || isFlagged(c)) {
    return { borderClass: 'border-l-4 border-amber-500', label: 'Needs review', state: 'needsReview' };
  }
  return { borderClass: 'border-l-4 border-gray-300', label: 'Not yet checked', state: 'neutral' };
}

/**
 * Legend for the catch card border colours (#144 R4). Colour is never the
 * only signal: every card also carries a text label, this legend just
 * explains what the colour means.
 */
const CatchStatusLegend: React.FC = () => (
  <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
    <span className="font-semibold text-gray-900">Card colours:</span>
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className="h-3 w-3 rounded-sm border-2 border-red-500 bg-white" />
      Disqualified
    </span>
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className="h-3 w-3 rounded-sm border-2 border-amber-500 bg-white" />
      Needs review (placed or flagged, not checked)
    </span>
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className="h-3 w-3 rounded-sm border-2 border-green-500 bg-white" />
      Checked
    </span>
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className="h-3 w-3 rounded-sm border-2 border-gray-300 bg-white" />
      Not flagged, not yet checked
    </span>
  </div>
);

export default CatchStatusLegend;
